import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  CircularProgress,
  IconButton,
  InputBase,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import SearchIcon from "@mui/icons-material/Search";
import { useSelectedItemStore } from "../store/useSelectedItemStore";
import { useNavigate } from "react-router-dom";
import { RoutesConfig } from "../router/types";
import { toast } from "react-toastify";
import { getAllClients } from "../services/clientService";

interface ClientCardProcessProps {
  listClient?: Client[];
}

export type Client = {
  INSC_ESTADUAL: string | null;
  IN_DEM_PIS_Q_COMP_RESUMO: "S" | "N";
  IN_CAPATAZIA_FRETE_DAI_SEFAZ: "S" | "N";
  ALIQ_CONT_IRRF: number;
  CD_ATIV_ECONOMICA: number;
  IN_DEM_TSA_SUFRAMA_Q_COMP: "S" | "N";
  IN_LETRA_PLI: "S" | "N";
  CEP: string;
  NR_ULTIMA_NF: number | null;
  DEDUCOES_SOCIAIS: "S" | "N";
  IN_USA_MATRIZ_TRIBUTACAO: "S" | "N";
  NUMERO: string;
  USER_ALTERACAO: string | null;
  SISCOMEX_CONTA: string;
  IN_EMPRESA_INDUSTRIA: "S" | "N";
  LETRA_PLI: string;
  INSC_MUNICIPAL: string;
  CD_MUNICIPIO: string;
  CALCULA_IMP_IRRF_10: "S" | "N";
  BAIRRO: string;
  E_MAIL_CLIENTES: string | null;
  VL_COMISSAO: number;
  IN_INCLUIR_TSA_SUFRAMA_DAI: "S" | "N";
  DT_EMISSAO_NF: string | null;
  INSC_MIN_AGRICULTURA: string | null;
  SISCOMEX_AGENCIA: string;
  CPF_REPRESENTANTE: string;
  CD_LOC_ARMAZENAGEM: string;
  LIMITE_DIAS_BOLETO: number;
  NOME: string;
  PLI_SUFRAMA: number;
  INSC_CNPJ: string;
  TELEFONE: string;
  CIDADE: string;
  ALIQ_CONT_ISS: number;
  CD_URF_DESPACHO: string;
  EMAIL: string;
  IN_TX_SISCOMEX_ANTIGA: "S" | "N";
  INSC_SUFRAMA: string | null;
  VL_ULTIMA_NF: number;
  SIGLA_PLI_SUFRAMA: string;
  SISCOMEX_BANCO: string;
  UF: string;
  CD_MOEDA_SEGURO: string;
  CD_RECINTO_ALFAND: string;
  IN_UP_TX_ANTIGA: "S" | "N";
  IN_DEM_PIS_Q_COMP_ANALITIC0: "S" | "N";
  ENDERECO: string;
  FAX: string | null;
  CD_PLI_FABRICANTE: number;
  ALIQ_CONT_ISS_M: number | null;
  USER_CADASTRO: string;
  IN_EXONERA_ICMS: "S" | "N";
  CD_URF_ENTRADA: string;
  CODIGO: number;
  DT_CADASTRO: Record<string, never>;
  NR_PROCESSO_TX_SISCOMEX: string | null;
  OBSERVACAO: string | null;
  NM_FORMULA_SEGURO: string | null;
  CD_SETOR_ALFAND: string;
  CPL_ENDERECO: string;
  DT_ALTERACAO: string | null;
  QTD_ITENS_NOTA: number;
  IN_UNICO_NR_PLI: "S" | "N";
};

const ClientCardProcess: React.FC<ClientCardProcessProps> = (
  props: ClientCardProcessProps
) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchText, setSearchText] = useState("");
  const [searchClient, setSearchClient] = useState("");
  const setSelectedItemId = useSelectedItemStore((s) => s.setSelectedItemId);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const navigate = useNavigate();
  const listRef = React.useRef<HTMLDivElement>(null);

  const handleClientClick = (clientId: string) => {
    setSelectedItemId(clientId);
    navigate(RoutesConfig.PROCESSOSPORCLIENTE.path);
  };

  const [resultAllClients, setResultAllClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [allClientsLoaded, setAllClientsLoaded] = useState(false);

  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const isAutoLoading = useRef(false);

  const getClientProcesses = async () => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);
    try {
      const data = await getAllClients(nextCursor);

      setResultAllClients((prevProcesses: any) => {
        const newClients = data.items.filter(
          (newClient: any) =>
            !prevProcesses.some(
              (existing: any) => existing.CODIGO === newClient.CODIGO
            )
        );
        return [...prevProcesses, ...newClients];
      });

      setNextCursor(data.nextCursor);
      setHasMore(data.hasNextPage);
    } catch (err) {
      toast.error("Erro ao buscar processos do cliente");
      console.error("Erro ao buscar processos do cliente:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getClientProcesses();
  }, []);

  useEffect(() => {
    if (resultAllClients && resultAllClients.length > 0 && isFirstLoad) {
      setIsFirstLoad(false);
    }
  }, [resultAllClients]);

  const handleLoadMore = () => {
    if (hasMore && nextCursor) {
      getClientProcesses();
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const container = listRef.current;
      if (!container || isLoading || !hasMore) return;

      const { scrollTop, scrollHeight, clientHeight } = container;

      if (scrollTop + clientHeight >= scrollHeight - 100) {
        handleLoadMore();
      }
    };

    const container = listRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [isLoading, hasMore]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSearchClient(searchText);
  };

  const fetchAllClientsOnce = async () => {
    let allClients: Client[] = [];
    let cursor = null;
    let hasMoreResults = true;

    while (hasMoreResults) {
      const data: any = await getAllClients(cursor);
      allClients = [
        ...allClients,
        ...data.items.filter(
          (newClient: Client) =>
            !allClients.some((c) => c.CODIGO === newClient.CODIGO)
        ),
      ];
      cursor = data.nextCursor;
      hasMoreResults = data.hasNextPage;
    }

    setResultAllClients(allClients);
    setAllClientsLoaded(true);
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setSearchText(text);

    if (text.trim() === "") return;

    if (!allClientsLoaded && !isLoading) {
      setIsLoading(true);
      try {
        await fetchAllClientsOnce();
      } catch (error) {
        toast.error("Erro ao carregar todos os clientes.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const pegarPrimeiraESegundaPalavra = (texto: string) => {
    if (!texto) return "";
    const palavras = texto.split(/[,\s]+/);
    return palavras.slice(0, 2).join(" ");
  };

  const filteredClients = React.useMemo(() => {
    if (!searchText.trim()) return resultAllClients;
    return resultAllClients.filter((client) =>
      client.NOME.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [searchText, resultAllClients]);

  const autoLoadCount = useRef(0);

  useEffect(() => {
    const checkContentHeight = () => {
      const contentHeight = document.body.scrollHeight;
      const windowHeight = window.innerHeight;

      if (
        hasMore &&
        contentHeight <= windowHeight &&
        autoLoadCount.current < 4
      ) {
        autoLoadCount.current += 1;
        handleLoadMore();
      }
    };

    const timeout = setTimeout(checkContentHeight, 200);
    return () => clearTimeout(timeout);
  }, [resultAllClients.length, hasMore]);

  return (
    <>
      <Box
        sx={{
          border: "1px solid #0A0C33",
          borderRadius: "15px",
          padding: "28px",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "80vh",
          cursor: "pointer",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            gap: isMobile ? 2 : 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1,
            }}
          >
            <AccountCircleIcon
              sx={{ color: "#0A0C33", height: "31px", width: "27px" }}
            />
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", color: "#0A0C33" }}
            >
              Clientes
            </Typography>
          </Box>
          <Paper
            component="form"
            sx={{
              display: "flex",
              alignItems: "center",
              borderRadius: "20px",
              padding: "2px 10px",
              width: isMobile ? "100%" : 300,
              backgroundColor: "#F0F0F0",
              marginTop: isMobile ? "8px" : 0,
            }}
            onSubmit={handleSubmit}
          >
            <InputBase
              placeholder="Buscar cliente"
              sx={{ ml: 1, flex: 1 }}
              inputProps={{ "aria-label": "buscar processo" }}
              value={searchText}
              onChange={handleInputChange}
            />
            <IconButton
              type="submit"
              sx={{ p: "10px", color: "#0A0C33 " }}
              aria-label="search"
            >
              <SearchIcon />
            </IconButton>
          </Paper>
        </Box>
        <Box
          ref={listRef}
          sx={{
            marginTop: "39px",
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              borderRadius: "12px",
            }}
          >
            {filteredClients.map((client: any) => (
              <Card
                key={client.CODIGO}
                sx={{
                  display: "flex",
                  p: 2,
                  mb: 1,
                  borderRadius: "12px",
                  position: "relative",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 24,
                    bgcolor: "#326AFD",
                    borderTopLeftRadius: 8,
                    borderBottomLeftRadius: 8,
                  },
                }}
                onClick={() => handleClientClick(client.CODIGO)}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ textAlign: "left", ml: "32px" }}
                  >
                    {pegarPrimeiraESegundaPalavra(client?.NOME)}{" "}
                    <Typography
                      component="span"
                      color="text.secondary"
                      fontSize="14px"
                    >
                      {" "}
                      ID: {client.CODIGO}
                    </Typography>
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.5,
                      textAlign: "left",
                      ml: "32px",
                    }}
                  >
                    CNPJ: {client.INSC_CNPJ}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      textAlign: "left",
                      ml: "32px",
                    }}
                  >
                    {client.ENDERECO}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      textAlign: "left",
                      ml: "32px",
                    }}
                  >
                    CEP: {client.CEP}
                  </Typography>
                </Box>
              </Card>
            ))}
          </Box>
          {isLoading && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              padding={4}
            >
              <CircularProgress
                size={48}
                thickness={4}
                style={{ color: "#326AFD" }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default ClientCardProcess;
